import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IZoomDAO } from '../../../../../api/zoom.dao';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { OpenFrom, TRACKING_EVENTS, CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { IEventRequestParams } from '../../../../../api/shared.dto';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';

class WebinarReportsViewModel {
    @mobx.observable
    public isWebinarReportLoading: boolean = false;

    constructor(
        public readonly accountId: number,
        private readonly eventId: number,
        private readonly openFrom: OpenFrom,
        @IoC.Inject private readonly zoomDao: IZoomDAO = IoC.Container.get(IZoomDAO),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
    ) { }

    @mobx.action
    // eslint-disable-next-line @typescript-eslint/no-shadow
    private getWebinarReport = async (
        params: IEventRequestParams & { webinarId: string },
        getReportEndpoint: ({ webinarId }) => Promise<void>,
        generateReportErrorMessage: string,
        eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS | TRACKING_EVENTS,
        eventBody?: object
    ): Promise<void> => {
        try {
            this.isWebinarReportLoading = true;
            await getReportEndpoint.call(this.zoomDao, params);
            this.trackingService.get().track(
                eventName,
                eventBody || {
                    'Open From': this.openFrom,
                }
            );
        } catch (e) {
            const errorMessage = e.response.status === 412
                ? 'This Webinar does not have any Registrants yet'
                : generateReportErrorMessage;
            mobx.runInAction(() => {
                alertServerError('Error', errorMessage);
                this.trackingService.get().track(eventName, {
                    errorMessage: errorMessage,
                    'Open From': this.openFrom,
                });
            });
        } finally {
            mobx.runInAction(() => {
                this.isWebinarReportLoading = false;
            });
        }
    };

    public getWebinarAttendeeReport = async (webinarId: string): Promise<void> => {
        this.getWebinarReport(
            {
                accountId: this.accountId,
                eventId: this.eventId,
                webinarId: webinarId,
            },
            this.zoomDao.getWebinarAttendeeReport,
            'The report could not be generated. Please ensure that the Webinar ID is valid, and that the Webinar has ended, and try again.',
            CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.WEBINAR_CONNECTION_REPORT_ATTENDEE
        );
    };

    public getWebinarRegistrationReport = async (webinarId: string): Promise<void> => {
        this.getWebinarReport(
            {
                accountId: this.accountId,
                eventId: this.eventId,
                webinarId: webinarId,
            },
            this.zoomDao.getWebinarRegistrationReport,
            'The report could not be generated. Please ensure that the Webinar ID is valid, and try again.',
            TRACKING_EVENTS.GENERATE_ZOOM_REGISTRATION_REPORT,
            {
                'Account Name': this.globalStorage.getEvent()?.accountName,
                'Event ID': this.eventId,
                'Open From': this.openFrom,
            }
        );
    };
}

export { WebinarReportsViewModel };
